import DateTimeRangePicker, {
  type DateTimeRangePickerProps,
} from '@wojtekmaj/react-datetimerange-picker';
import '@wojtekmaj/react-datetimerange-picker/dist/DateTimeRangePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';

type DateTimeRangeValue = Parameters<NonNullable<DateTimeRangePickerProps['onChange']>>[0];

export interface DateTimeRangeFilterProps {
  label: string;
  from: string;
  to: string;
  onRangeChange: (from: string, to: string) => void;
}

function parseDateTimeValue(value: string): Date | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : new Date(timestamp);
}

function serializeDateTimeValue(value: Date | null): string {
  if (!value) return '';
  return value.toISOString();
}

function normalizeRangeValue(value: DateTimeRangeValue): [Date | null, Date | null] {
  if (Array.isArray(value)) {
    return [value[0], value[1]];
  }

  return [value, null];
}

export function DateTimeRangeFilter({ label, from, to, onRangeChange }: DateTimeRangeFilterProps) {
  const hasRangeValue = Boolean(from || to);

  return (
    <div
      className={`flex w-full flex-col gap-1 text-small font-medium text-neutral-700 ${
        hasRangeValue
          ? 'min-w-[320px] max-w-[360px] sm:w-[360px]'
          : 'min-w-[160px] max-w-[180px] sm:w-[180px]'
      }`}
    >
      {label}
      <DateTimeRangePicker
        value={[parseDateTimeValue(from), parseDateTimeValue(to)]}
        onChange={(nextValue) => {
          const [nextFrom, nextTo] = normalizeRangeValue(nextValue);
          onRangeChange(serializeDateTimeValue(nextFrom), serializeDateTimeValue(nextTo));
        }}
        calendarAriaLabel={`Toggle ${label.toLowerCase()} calendar`}
        clearAriaLabel={`Clear ${label.toLowerCase()} range`}
        rangeDivider={<span className="px-1 text-neutral-500">to</span>}
        format="y-MM-dd HH:mm"
        showLeadingZeros
        disableClock={false}
        nativeInputAriaLabel={label}
        className={`backoffice-date-time-range-picker ${
          hasRangeValue
            ? 'backoffice-date-time-range-picker--filled'
            : 'backoffice-date-time-range-picker--empty'
        }`}
      />
    </div>
  );
}
